import React, { CSSProperties, ReactNode } from "react";
import ExpandCollapse from "common/Animations/ExpandCollapse";
import styled, { css, useTheme } from "styled-components";
import Switch from "common/Buttons/Switch";
import Text from "common/Typography/Text";
import {
  OverrideType,
  TitleWithToggleProps,
} from "@shared/features/bots/BotForm/common/types";
import Button from "common/Buttons/Button";
import { useTitleWithToggle } from "@shared/features/bots/BotForm/useTitleWithToggle";
import OverrideTypeToggle from "features/bots/BotForm/common/OverrideTypeToggle";
import HighlightedText from "common/Text/HighlightedText";
import { USING_CHANNEL_SETTING } from "@shared/features/bots/constants";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import { LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { hexToRGB } from "@shared/utils/misc";
import { phoneMaxWidth } from "common/NewLook/layout.ts";

const TitleWithToggle = ({
  title,
  content,
  size = "small",
  fieldType,
  hideWithOpacity,
  showMultipleValues: propsShowMultipleValues,
  overrideObjectName: propsOverrideObjectName,
  getDefaultValue,
  onPersonalClick,
  onOverrideChange,
  style,
  titleStyle,
  contentStyle,
  defaultChannelValue,
  hideToggle: propsHideToggle,
  disableOverrideButton,
  onDisabledOverrideButtonClick,
  overrideButtonWrapperTestId,
}: TitleWithToggleProps & {
  style?: CSSProperties;
  titleStyle?: CSSProperties;
  contentStyle?: CSSProperties;
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { isNewLook } = useTheme();
  const {
    checked,
    expanded,
    hideContentWithOpacity,
    baseOpacity,
    isOpacityWrapper,
    overrideButtonText,
    onToggleChange,
    showOverrideButton,
    overrideTypeToggleData,
    channelOverrideDescription,
    hideToggle,
    isLargeChannelValueWarning,
    helperData,
  } = useTitleWithToggle({
    fieldType,
    hideWithOpacity,
    overrideObjectName: propsOverrideObjectName,
    isMultiValue: propsShowMultipleValues,
    getDefaultValue,
    onPersonalClick,
    onOverrideChange,
    defaultChannelValue,
  });
  const isChannelValue = overrideTypeToggleData.value === OverrideType.CHANNEL;
  const isDisabledClickThrough =
    !!disableOverrideButton && !!onDisabledOverrideButtonClick;

  const getToggle = () => {
    if (showOverrideButton || hideToggle || propsHideToggle) return undefined;
    const style = title ? { marginLeft: 12 } : { marginLeft: 0 };
    if (overrideTypeToggleData.shouldDisplay)
      return (
        <OverrideTypeToggle
          onClick={overrideTypeToggleData.onChange}
          options={overrideTypeToggleData.options}
          style={style}
          selectedOption={overrideTypeToggleData.value}
          showPersonalAsOn={overrideTypeToggleData.showPersonalAsOn}
        />
      );
    return (
      <Switch
        style={{ margin: "0 4px 0 12px" }}
        size={size}
        onChange={onToggleChange}
        checked={checked}
      />
    );
  };
  const toggle = getToggle();
  const showTitleWrapper = !!title || !!helperData || !!toggle;

  const showChannelWarning = isChannelValue && isLargeChannelValueWarning;
  const getChannelOverrideOverlay = () => {
    if (!showChannelWarning) return;
    return (
      <ChannelValueMessageWrapper>
        <HighlightedText hideLine={true}>
          <span style={{ fontWeight: 600, display: "block" }}>
            {t(USING_CHANNEL_SETTING)}
          </span>
          {channelOverrideDescription}
        </HighlightedText>
      </ChannelValueMessageWrapper>
    );
  };

  let wrappedContent: ReactNode;
  const marginTop = title && content ? 12 : 0;
  if (isOpacityWrapper) {
    wrappedContent = (
      <div
        style={{
          position: "relative",
          minHeight: showOverrideButton ? 60 : showChannelWarning ? 150 : 0,
          marginTop,
        }}
      >
        <OpacityWrapper
          $checked={!hideContentWithOpacity}
          $baseOpacity={baseOpacity}
        >
          {content}
        </OpacityWrapper>
        {showOverrideButton && (
          <OverrideButtonWrapper data-testid={overrideButtonWrapperTestId}>
            <StyledButton
              type="primary"
              disabled={disableOverrideButton && !isDisabledClickThrough}
              aria-disabled={disableOverrideButton || undefined}
              $disabledClickThrough={isDisabledClickThrough}
              onClick={
                disableOverrideButton
                  ? onDisabledOverrideButtonClick
                  : () => overrideTypeToggleData.onChange?.()
              }
            >
              {overrideButtonText}
            </StyledButton>
          </OverrideButtonWrapper>
        )}
        {getChannelOverrideOverlay()}
      </div>
    );
  } else {
    wrappedContent = (
      <ExpandCollapse
        expanded={expanded}
        style={{ width: "100%", marginTop, ...contentStyle }}
        hideOnCollapse={false}
      >
        {content}
      </ExpandCollapse>
    );
  }

  return (
    <div style={{ width: "100%", ...style }}>
      {showTitleWrapper && (
        <TitleWrapper as="div" style={titleStyle}>
          {title}
          {helperData && <HelperTooltip {...helperData} />}
          {isNewLook && toggle ? <ToggleSlot>{toggle}</ToggleSlot> : toggle}
        </TitleWrapper>
      )}
      {wrappedContent}
    </div>
  );
};

export default TitleWithToggle;

const TitleWrapper = styled(Text)`
  display: flex;
  align-items: center;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      min-height: 24px;
      color: ${p.theme.colors.primaryText};
      font-size: 13px;
      font-weight: ${p.theme.typography.fontWeights.MEDIUM};
      /* Phone: a row name at the body size, not heavier than the fields. */
      @media (max-width: ${phoneMaxWidth}) {
        font-size: 14px;
        line-height: 20px;
        font-weight: 450;
      }
    `};
`;
/** New look: the switch sits at the end of the row, like a settings list. */
const ToggleSlot = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: auto;
  padding-left: 12px;
  > * {
    margin-left: 0 !important;
  }
`;
const OpacityWrapper = styled.div<{ $checked: boolean; $baseOpacity?: number }>`
  position: relative;
  transition: opacity 0.25s ease-in-out;
  opacity: ${(p) => {
    if (p.$checked) return 1;
    const baseOpacity = p.$baseOpacity || 0.35;
    return baseOpacity - (p.theme.isDarkMode ? 0.1 : 0);
  }};
  pointer-events: ${(p) => (p.$checked ? "inherit" : "none")};
  display: flex;
  flex-direction: column;
  z-index: 2;
`;
const centerOverlayContent = css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const OverrideButtonWrapper = styled.div`
  ${centerOverlayContent};
`;
const StyledButton = styled(Button)<{ $disabledClickThrough?: boolean }>`
  ${(p) =>
    p.$disabledClickThrough &&
    css`
      &&.ant-btn {
        opacity: 1;
        cursor: pointer;
        background: linear-gradient(
            ${hexToRGB(p.theme.colors.primaryBlue, 0.5)},
            ${hexToRGB(p.theme.colors.primaryBlue, 0.5)}
          ),
          ${p.theme.colors.cardBgColor} !important;
        border-color: ${p.theme.colors.midBlue} !important;
        color: ${p.theme.colors.buttonText} !important;
        box-shadow: none;
      }

      &&.ant-btn:hover {
        background: linear-gradient(
            ${hexToRGB(p.theme.colors.primaryBlue, 0.5)},
            ${hexToRGB(p.theme.colors.primaryBlue, 0.5)}
          ),
          ${p.theme.colors.cardBgColor} !important;
        border-color: ${p.theme.colors.midBlue} !important;
        color: ${p.theme.colors.buttonText} !important;
      }
    `}
`;
const ChannelValueMessageWrapper = styled.div`
  ${centerOverlayContent};
  width: 80%;
  max-width: 450px;
`;
